import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { motion } from 'framer-motion';
import { ZoomIn, ZoomOut, X } from 'lucide-react';
import { HERO_ASPECT } from '../../utils/heroCrop';
import './ImageCropModal.css';

const ImageCropModal = ({
  imageSrc,
  title = 'Crop for hero section',
  aspect = HERO_ASPECT,
  showInHero: initialShowInHero = true,
  onConfirm,
  onCancel,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showInHero, setShowInHero] = useState(initialShowInHero);
  const [submitting, setSubmitting] = useState(false);

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setSubmitting(true);
    try {
      await onConfirm({ croppedAreaPixels, showInHero });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      className="image-crop-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.div
        className="image-crop-modal"
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="image-crop-header">
          <div>
            <h2>{title}</h2>
            <p>Drag to reposition · Scroll or slider to zoom · Crop matches the hero banner shape</p>
          </div>
          <button type="button" className="image-crop-close" onClick={onCancel} aria-label="Close">
            <X size={22} />
          </button>
        </div>

        <div className="image-crop-area">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="image-crop-controls">
          <label className="image-crop-zoom-label">
            <ZoomOut size={16} />
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
            <ZoomIn size={16} />
          </label>

          <label className="image-crop-hero-toggle">
            <input
              type="checkbox"
              checked={showInHero}
              onChange={(e) => setShowInHero(e.target.checked)}
            />
            <span>Show in homepage hero slideshow</span>
          </label>
        </div>

        <div className="image-crop-footer">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleConfirm} disabled={submitting || !croppedAreaPixels}>
            {submitting ? 'Saving…' : 'Apply crop & save'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ImageCropModal;
